import {
  CalendarCheck,
  FileText,
  MessageCircleQuestion,
} from "lucide-react-native";
import { Platform, ScrollView, useWindowDimensions, View } from "react-native";
import {
  ActionRowCard,
  Dialog,
  DialogHeader,
  DialogIcon,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import type {
  CyclePregnancyFollowUp,
  PregnancyFollowUpStatus,
} from "../../pregnancy";

const PREGNANCY_SETUP_ILLUSTRATION =
  require("@src/apps/moon/assets/images/calendar-reminder-dialog-illustration.png") as number;

type Props = {
  visible: boolean;
  /** The server-held follow-up, or null while it is unread or absent. */
  followUp: CyclePregnancyFollowUp | null;
  onClose: () => void;
  onAction: (action: PlanAction) => void;
};

function TrackingActionRow({
  icon,
  title,
  detail,
  onPress,
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  onPress: () => void;
}) {
  return (
    <ActionRowCard
      leading={icon}
      title={title}
      subtitle={detail}
      onPress={onPress}
      accessibilityLabel={`${title}. ${detail}`}
      style={{ marginBottom: 0 }}
    />
  );
}

export function PregnancyTrackingSetupDialog({
  visible,
  followUp,
  onClose,
  onAction,
}: Props) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  const isOptedIn: boolean = followUp?.status === "opted_in";

  /**
   * Bounded against the window, with no floor.
   *
   * The dialog used to ask for `maxHeight: "92%"` and nothing else. A percentage
   * resolves against a parent that has no height of its own here, so nothing
   * constrained the surface: three action rows, a header and an illustration
   * simply grew past the window, and the container's `overflow: hidden` cut the
   * footer in half — the patient saw "Activer" and "Pas maintenant" sliced by the
   * card's edge with no way to reach them.
   */
  const dialogMaxHeight = Math.min(760, height * 0.9);
  const shared =
    "homepage.review_concept.post_procedure_outcome.pregnancy_tracking";

  const navigate = (action: PlanAction) => {
    onClose();
    onAction(action);
  };

  const scheduleAction: PlanAction = {
    label: t(`${shared}.action_appointment`),
    screen: "CareOverview",
    params: { focus: "appointment" },
  };
  const questionAction: PlanAction = {
    label: t(`${shared}.action_question`),
    signal: "open-ai-chat",
    params: { initialPrompt: t(`${shared}.action_question_prompt`) },
  };
  /**
   * The cycle the result belongs to, on the tab the result is on.
   *
   * A `CycleDetailScreen` action carrying no cycleId is read as a request to
   * create one (`resolveCycleDetailAction`), which is how "review the recorded
   * result" used to open the start-treatment flow and be met with "you already
   * have a cycle open". The signal resolves the destination on the screen, which
   * is the only place that knows which cycle the result belongs to.
   */
  const recordsAction: PlanAction = {
    label: t(`${shared}.action_records`),
    signal: "open-recorded-cycle",
    params: { focus: "outcome" },
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
      containerStyle={{ maxHeight: dialogMaxHeight }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{ maxHeight: dialogMaxHeight }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        {/* The whole surface scrolls. It used to be bounded around a footer of
            two buttons; those are gone — the three rows below are the dialog's
            actions now — but the bound is still what keeps the card inside the
            window rather than sliced by its own edge. */}
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 14 }}
        >
          <View style={{ alignItems: "center", gap: 8, paddingTop: 4 }}>
            <DialogIcon
              source={PREGNANCY_SETUP_ILLUSTRATION}
              width={62}
              height={64}
              style={{ marginBottom: 2 }}
              testID="pregnancy-tracking-illustration"
            />
            <DialogHeader
              title={
                isOptedIn ? t(`${shared}.opted_in_title`) : t(`${shared}.title`)
              }
              subtitle={
                isOptedIn
                  ? t(`${shared}.opted_in_summary`)
                  : t(`${shared}.summary`)
              }
              style={{ marginBottom: 0 }}
            />
          </View>

          <View style={{ gap: 10 }}>
            <TrackingActionRow
              icon={
                <CalendarCheck
                  size={22}
                  color={EMBER.colors.text}
                  strokeWidth={1.8}
                />
              }
              title={t(`${shared}.action_appointment`)}
              detail={t(`${shared}.action_appointment_detail`)}
              onPress={() => navigate(scheduleAction)}
            />
            <TrackingActionRow
              icon={
                <MessageCircleQuestion
                  size={22}
                  color={EMBER.colors.text}
                  strokeWidth={1.8}
                />
              }
              title={t(`${shared}.action_question`)}
              detail={t(`${shared}.action_question_detail`)}
              onPress={() => navigate(questionAction)}
            />
            <TrackingActionRow
              icon={
                <FileText
                  size={22}
                  color={EMBER.colors.text}
                  strokeWidth={1.8}
                />
              }
              title={t(`${shared}.action_records`)}
              detail={t(`${shared}.action_records_detail`)}
              onPress={() => navigate(recordsAction)}
            />
          </View>
        </ScrollView>
      </View>
    </Dialog>
  );
}

export function pregnancyTrackingSetupStatusLabel(
  t: (key: string) => string,
  status: PregnancyFollowUpStatus | null,
): string {
  return status === "opted_in"
    ? t(
        "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.action_review",
      )
    : t(
        "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.action_open",
      );
}
